import React, { FC, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";

import { getCurrentUser } from "../../../data/user";
import { DashboardMeasurement } from "../../../types/VesselGasoil";

type Props = {
  open: boolean;
  vesselId?: string | number | null;
  initialData?: DashboardMeasurement | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const API_URL = process.env.REACT_APP_API_URL;
const APP_URL = process.env.REACT_APP_URL || "";

const getEntityId = (value?: any) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.split("/").filter(Boolean).pop() || value;
  }

  if (value["@id"]) {
    return String(value["@id"]).split("/").filter(Boolean).pop() || value["@id"];
  }

  if (value.id !== undefined && value.id !== null) {
    return String(value.id);
  }

  return "";
};

const getApiIri = (resource: string, id?: string | number | null) => {
  if (!id) return "";

  const stringId = String(id);

  if (stringId.startsWith("/api/")) {
    return stringId;
  }

  if (stringId.startsWith("http://") || stringId.startsWith("https://")) {
    return stringId;
  }

  return `/api/${resource}/${stringId}`;
};

const getUserIri = (user?: any) => {
  if (!user) return "";

  if (typeof user === "string") {
    if (user.startsWith("/api/")) {
      return user;
    }

    const userId = getEntityId(user);

    return userId ? `/api/users/${userId}` : "";
  }

  if (user["@id"]) {
    return user["@id"];
  }

  if (user.id !== undefined && user.id !== null) {
    return `/api/users/${user.id}`;
  }

  return "";
};

const getAuthHeaders = () => ({
  "Content-Type": "application/ld+json; charset=utf-8",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const fetchCurrentUserDirectly = async () => {
  const response = await axios({
    method: "get",
    url: `${API_URL}/users?page=1`,
    headers: getAuthHeaders(),
  });

  return (
    response.data?.["hydra:member"]?.[0] ||
    response.data?.member?.[0] ||
    response.data?.data?.[0] ||
    null
  );
};

const toDatetimeLocalValue = (value?: string | null) => {
  if (!value) return dayjs().format("YYYY-MM-DDTHH:mm");

  const parsed = dayjs(value);

  return parsed.isValid()
    ? parsed.format("YYYY-MM-DDTHH:mm")
    : dayjs().format("YYYY-MM-DDTHH:mm");
};

const GasoilDialogForm: FC<Props> = ({
  open,
  vesselId,
  initialData = null,
  onClose,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const currentUser = useSelector((state: any) => {
    return (
      state.user?.data ||
      state.users?.data ||
      state.currentUser?.data ||
      state.auth?.user ||
      null
    );
  });

  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DDTHH:mm"));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const editId = useMemo(() => getEntityId(initialData), [initialData]);
  const editing = Boolean(editId);

  useEffect(() => {
    if (!open) return;

    if (!getUserIri(currentUser)) {
      dispatch<any>(getCurrentUser());
    }
  }, [open, currentUser, dispatch]);

  useEffect(() => {
    if (!open) return;

    setAmount(
      initialData?.amount !== null && initialData?.amount !== undefined
        ? String(initialData.amount)
        : ""
    );

    setDate(toDatetimeLocalValue(initialData?.date));
    setError("");
  }, [open, initialData]);

  const submit = async () => {
    const parsedAmount = Number(amount);

    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError(t("invalidAmount", "Please enter a valid amount."));
      return;
    }

    if (!date) {
      setError(t("dateRequired", "Date is required."));
      return;
    }

    const vesselIri = getApiIri("vessels", vesselId);

    if (!vesselIri) {
      setError(t("missingVessel", "Missing vessel."));
      return;
    }

    try {
      setSaving(true);
      setError("");

      let userIri = getUserIri(currentUser);

      if (!userIri) {
        const fetchedUser = await fetchCurrentUserDirectly();
        userIri = getUserIri(fetchedUser);
      }

      if (!userIri) {
        setError(t("missingUser", "Missing current user."));
        return;
      }

      const payload = {
        amount: parsedAmount,
        date: dayjs(date).toISOString(),
        vessel: vesselIri,
        user: userIri,
      };

      if (editing) {
        const iri = initialData?.["@id"] || `/api/vessel_gasoils/${editId}`;

        await axios({
          method: "put",
          url: `${APP_URL}${iri}`,
          headers: getAuthHeaders(),
          data: payload,
        });
      } else {
        await axios({
          method: "post",
          url: `${API_URL}/vessel_gasoils`,
          headers: getAuthHeaders(),
          data: payload,
        });
      }

      await onSaved();
      onClose();
    } catch (exception: any) {
      setError(
        exception?.response?.data?.["hydra:description"] ||
          exception?.response?.data?.detail ||
          exception?.message ||
          t("saveFailed", "Saving failed.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ fontWeight: 950 }}>
        {editing
          ? t("editGasoilMeasurement", "Edit gasoil measurement")
          : t("addGasoilMeasurement", "Add gasoil measurement")}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label={t("amountLiters", "Amount in liters")}
            type="number"
            value={amount}
            disabled={saving}
            fullWidth
            inputProps={{
              min: 0,
              step: "0.01",
            }}
            onChange={(event) => setAmount(event.target.value)}
          />

          <TextField
            label={t("dateTime", "Date and time")}
            type="datetime-local"
            value={date}
            disabled={saving}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(event) => setDate(event.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel", "Cancel")}
        </Button>

        <Button
          variant="contained"
          onClick={submit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            textTransform: "none",
            fontWeight: 850,
            borderRadius: "10px",
          }}
        >
          {saving ? t("saving", "Saving") : t("save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GasoilDialogForm;